import type { Ref } from 'vue'
import { toRef, watch } from 'vue'
import { useGtmVar } from '@/composables/app/gtm/useGtmVar'
import type {
    EngagementRingPageQuery,
    NonSkuGemstoneFragment,
    NonSkuLabGrownFragment,
} from '@/graphql/api'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import { assertNever } from '@/helpers'

const getLabGrownContentIds = (
    ring: NonNullable<EngagementRingPageQuery['catalogueEngagementRing']>,
    stone: NonSkuLabGrownFragment
) => {
    const catalogueCode = ring.catalogueCode
    const shape = stone.shape.translation.name
    const color = stone.color.translation.name
    const clarity = stone.clarity.translation.name
    const caratWeight = stone.caratWeight

    return `${catalogueCode}-lg-dia-${shape}-${color}-${clarity}-${caratWeight}ct`
}

const getGemstoneContentIds = (
    ring: NonNullable<EngagementRingPageQuery['catalogueEngagementRing']>,
    stone: NonSkuGemstoneFragment
) => {
    const catalogueCode = ring.catalogueCode
    const color = stone.color.translation.name
    const crystal = stone.crystal.translation.name
    const shape = stone.shape.translation.name
    const colorGrade = stone.colorGrade.translation.name
    const clarity = stone.clarity.translation.name
    const caratWeight = stone.caratWeight

    return `${catalogueCode}-nat-gs-${color}-${crystal}-${shape}-${colorGrade}-${clarity}-${caratWeight}ct`
}

export function useEngagementRingAnalytics(
    is3rdStep: Readonly<Ref<boolean>>,
    ring: Readonly<
        Ref<
            | EngagementRingPageQuery['catalogueEngagementRing']
            | null
            | undefined
        >
    >,
    ringBuilderStone: Readonly<Ref<RingBuilderStoneTypes | null>>,
    fetchingPageData: Readonly<Ref<boolean>>
): void {
    const isRingBuilderCompleted = useGtmVar(
        'is_ring_builder_completed',
        is3rdStep.value
    )

    watch(is3rdStep, (newIs3rdStep) => {
        if (newIs3rdStep) {
            isRingBuilderCompleted.value = true
        }
    })

    const contentIds = useGtmVar('content_ids', '')

    const fetching = toRef(() => fetchingPageData.value)

    watch(fetching, (newFetching) => {
        if (!newFetching) {
            const stoneType = ringBuilderStone.value?.__typename

            if (!stoneType || !ring.value) {
                return
            }

            if (is3rdStep.value) {
                switch (stoneType) {
                    case 'GemsApi_Stone':
                        contentIds.value = ring.value.catalogueCode
                        return
                    case 'NonSkuGemstone':
                        contentIds.value = getGemstoneContentIds(
                            ring.value,
                            ringBuilderStone.value
                        )
                        return
                    case 'NonSkuLabGrown':
                        contentIds.value = getLabGrownContentIds(
                            ring.value,
                            ringBuilderStone.value
                        )
                        return
                    default:
                        assertNever(stoneType, true)
                }
            } else {
                contentIds.value = ring.value.catalogueCode
            }
        }
    })
}
